import clsx from 'clsx';
import { type ReactElement } from 'react';

import type { DraftStats } from '../types';

interface DraftStatsPanelProps {
  stats: DraftStats;
}

const COLORS = [
  { key: 'W', label: 'White', color: 'bg-amber-100' },
  { key: 'U', label: 'Blue', color: 'bg-blue-400' },
  { key: 'B', label: 'Black', color: 'bg-zinc-600' },
  { key: 'R', label: 'Red', color: 'bg-red-500' },
  { key: 'G', label: 'Green', color: 'bg-green-500' },
  { key: 'C', label: 'Colorless', color: 'bg-zinc-400' },
] as const;

export default function DraftStatsPanel({ stats }: DraftStatsPanelProps): ReactElement {
  const maxCurveValue = Math.max(...Object.values(stats.manaCurve), 1);

  return (
    <div className="space-y-5">
      {/* Summary */}
      <div className="space-y-1">
        <h3 className="text-xs font-semibold tracking-wider text-zinc-400 uppercase">Pool ({stats.totalPicks})</h3>
        <div className="flex gap-3 text-sm">
          <span className="text-zinc-300">
            <span className="font-medium text-zinc-100">{stats.creatureCount}</span> creatures
          </span>
          <span className="text-zinc-300">
            <span className="font-medium text-zinc-100">{stats.averageCMC.toFixed(1)}</span> avg
          </span>
        </div>
      </div>

      {/* Mana Curve */}
      <div className="space-y-3">
        <h3 className="text-xs font-semibold tracking-wider text-zinc-400 uppercase">Mana Curve</h3>
        <div className="flex items-end gap-1" style={{ height: '72px' }}>
          {[1, 2, 3, 4, 5, 6, 7].map((cmc) => {
            const count = stats.manaCurve[cmc] || 0;
            const height = maxCurveValue > 0 ? (count / maxCurveValue) * 100 : 0;
            return (
              <div key={cmc} className="flex flex-1 flex-col items-center">
                {/* Bar with count positioned at top of fill */}
                <div className="relative flex w-full flex-col-reverse" style={{ height: '54px' }}>
                  <div className="relative w-full rounded-sm bg-blue-500/70 transition-all duration-200" style={{ height: `${height}%` }}>
                    {count > 0 && (
                      <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 text-[10px] font-medium text-zinc-400">{count}</span>
                    )}
                  </div>
                </div>
                {/* CMC label below */}
                <span className="mt-1 text-[10px] text-zinc-500">{cmc === 7 ? '7+' : cmc}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Color Distribution */}
      <div className="space-y-2">
        <h3 className="text-xs font-semibold tracking-wider text-zinc-400 uppercase">Colors</h3>
        <div className="space-y-1.5">
          {COLORS.map(({ key, color }) => {
            const count = stats.colorDistribution[key] || 0;
            if (count === 0) return null;
            const width = Math.max((count / (stats.totalPicks || 1)) * 100, 8);
            return (
              <div key={key} className="flex items-center gap-2">
                <span className="w-4 text-center text-xs font-medium text-zinc-400">{key}</span>
                <div className="h-3 flex-1 overflow-hidden rounded-full bg-zinc-800">
                  <div className={clsx('h-full rounded-full transition-all duration-300', color)} style={{ width: `${width}%` }} />
                </div>
                <span className="w-5 text-right text-xs text-zinc-500">{count}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Type Breakdown */}
      <div className="space-y-2">
        <h3 className="text-xs font-semibold tracking-wider text-zinc-400 uppercase">Types</h3>
        <div className="grid grid-cols-2 gap-1">
          {Object.entries(stats.typeBreakdown)
            .sort(([, a], [, b]) => b - a)
            .map(([type, count]) => (
              <div key={type} className="flex items-center justify-between rounded bg-zinc-800/50 px-2 py-1">
                <span className="text-xs text-zinc-400">{type}</span>
                <span className="text-xs font-medium text-zinc-200">{count}</span>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}
